import { Suspense, lazy, useEffect } from 'react';
import { Navigate, useSearchParams, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  let [searchParams, setSearchParams] = useSearchParams();
  const session = searchParams.get('session');

  useEffect(() => {
    if (session) {
      window.localStorage.setItem('token', session)
    }
  }, [session]);

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'forgot-password', element: <ForgotPassword /> },
        { path: ':context/set-password', element: <SetPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },
    {
      path: 'onboarding',
      element: (
        <AuthGuard>
          <LogoOnlyLayout/>
        </AuthGuard>
      ),
      children: [
        { path: '', element: <Onboarding /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'home', element: <GeneralApp /> },
        { path: 'ecommerce', element: <GeneralEcommerce /> },
        { path: 'analytics', element: <GeneralAnalytics /> },
        { path: 'financing', element: <FinancingSummary /> },
        { path: 'transactions', element: <Transactions /> },
        { path: 'credits', element: <Credits /> },
        { path: 'accounts', element: <AccountsManagement /> },
        { path: 'leads', element: <LeadsManagement /> },
        { path: 'booking', element: <GeneralBooking /> },

        {
          path: 'e-commerce',
          children: [
            { element: <Navigate to="/dashboard/e-commerce/shop" replace />, index: true },
            { path: 'shop', element: <EcommerceShop /> },
            { path: 'product/:name', element: <EcommerceProductDetails /> },
            { path: 'list', element: <EcommerceProductList /> },
            { path: 'product/new', element: <EcommerceProductCreate /> },
            { path: 'product/:name/edit', element: <EcommerceProductCreate /> },
            { path: 'checkout', element: <EcommerceCheckout /> },
            { path: 'invoice', element: <EcommerceInvoice /> },
          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            { path: 'profile', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'partner-offers',
          element: <PartnerOffers />
        },
        {
          path: 'qr-management',
          children: [
            { element: <GeneralApp /> },
            { path: 'create', element: <QrGenerator /> },
          ],
        },
        {
          path: 'faq-management',
          children: [
            { element: <Navigate to="/dashboard/faq-management/list" replace />, index: true },
            { path: 'list', element: <FaqList /> },
            { path: 'new', element: <FaqCreate /> },
            { path: ':slug/edit', element: <FaqCreate /> },
          ],
        },
        {
          path: 'help-center',
          children: [
            { element: <HelpCenter />, index: true },
            { path: ':slug', element: <HelpCenterArticle /> },
          ],
        },
        {
          path: 'underwriting',
          children: [
            { element: <Navigate to="/dashboard/underwriting/cnpl" replace />, index: true },
            { path: 'cnpl', element: <CnplUnderwriting /> },
          ],
        },
        {
          path: 'consults',
          children: [
            { element: <Navigate to="/dashboard/consults/list" replace />, index: true },
            { path: ':consultId/manage', element: <ConsultManage /> },
          ],
        },
        {
          path: 'blog',
          children: [
            { element: <Navigate to="/dashboard/blog/posts" replace />, index: true },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'post/:title', element: <BlogPost /> },
            { path: 'new-post', element: <BlogNewPost /> },
          ],
        },
        {
          path: 'mail',
          children: [
            { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
            { path: 'label/:customLabel', element: <Mail /> },
            { path: 'label/:customLabel/:mailId', element: <Mail /> },
            { path: ':systemLabel', element: <Mail /> },
            { path: ':systemLabel/:mailId', element: <Mail /> },
          ],
        },
        {
          path: 'chat',
          children: [
            { element: <StreamChat />, index: true },
            { path: 'new', element: <Chat /> },
            { path: ':conversationKey', element: <Chat /> },
          ],
        },
        {
          path: 'prescription',
          children: [
            { path: ':prescriptionId', element: <Prescription /> },
          ],
        },
        { path: 'calendar', element: <Calendar /> },
        { path: 'kanban', element: <Kanban /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'prescription/:prescriptionId/:securityToken', element: <Prescription /> },
        { path: 'casesheet/:casesheetId/:securityToken', element: <ConsultManage /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: 'calculator', element: <NoCostEmiCalculator /> },
        { path: 'manage-calls/:callId/:agentId', element: <CallManagement /> },
        { path: '500', element: <Page500 /> },
        { path: 'terms-and-conditions', element: <TnC /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <Navigate to="/dashboard/home" replace />,
      children: [
        { path: '', element: <Navigate to="/dashboard/home" replace /> },
        { element: <HomePage />, index: true },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ForgotPassword = Loadable(lazy(() => import('../pages/auth/ForgotPassword')));
const SetPassword = Loadable(lazy(() => import('../pages/auth/SetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));
const EcommerceInvoice = Loadable(lazy(() => import('../pages/dashboard/EcommerceInvoice')));
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
const FaqList = Loadable(lazy(() => import('../pages/dashboard/FaqList')));
const CnplUnderwriting = Loadable(lazy(() => import('../pages/dashboard/CnplUnderwriting')));
const FaqCreate = Loadable(lazy(() => import('../pages/dashboard/FaqCreate')));
const ConsultManage = Loadable(lazy(() => import('../pages/dashboard/ConsultManage')));
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));
const Prescription = Loadable(lazy(() => import('../pages/dashboard/Prescription')));
// Main
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const TnC = Loadable(lazy(() => import('../pages/TnC')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));

//GetStream.io Chat/Feed
const StreamChat = Loadable(lazy(() => import('../pages/dashboard/StreamChat')));

//Financing
const FinancingSummary = Loadable(lazy(() => import('../pages/dashboard/FinancingSummary')));
const Transactions = Loadable(lazy(() => import('../pages/dashboard/Transactions')));
const Credits = Loadable(lazy(() => import('../pages/dashboard/Credits')));

//Financing
const AccountsManagement = Loadable(lazy(() => import('../pages/dashboard/accounts/Management')));
const LeadsManagement = Loadable(lazy(() => import('../pages/dashboard/accounts/LeadManagement')));

//Partner Offers
const PartnerOffers = Loadable(lazy(() => import('../pages/dashboard/PartnerOffers')));

//Help Center
const HelpCenter = Loadable(lazy(() => import('../pages/dashboard/HelpCenter')));
const HelpCenterArticle = Loadable(lazy(() => import('../pages/dashboard/HelpCenterArticle')));

//QR Management
const QrGenerator = Loadable(lazy(() => import('../pages/dashboard/QrGenerator')));

// Onboarding
// const BasicTerms = Loadable(lazy(() => import('../pages/onboarding/BasicTerms')));
const Onboarding = Loadable(lazy(() => import('../pages/onboarding/Onboarding')));
// const ProviderType = Loadable(lazy(() => import('../pages/onboarding/ProviderType')));
// const SpecialtiesSelection = Loadable(lazy(() => import('../pages/onboarding/SpecialtiesSelection')));
// const ServicesSelection = Loadable(lazy(() => import('../pages/onboarding/ServicesSelection')));
// const PricingIndicator = Loadable(lazy(() => import('../pages/onboarding/Pricing')));
// const AgreementSigning = Loadable(lazy(() => import('../pages/onboarding/AgreementSigning')));
// const CompleteTerms = Loadable(lazy(() => import('../pages/onboarding/CompleteTerms')));
const NoCostEmiCalculator = Loadable(lazy(() => import('../pages/NoCostEmiCalculator')));

// Contact Center
const CallManagement = Loadable(lazy(() => import('../pages/CallManagement')));
